import {
  CustomTooltipInfo,
  Heading,
  Surface,
  SwitchTab,
} from '@quality24/design-system';
import classNames from 'classnames';
import * as React from 'react';

import {
  Customizations,
  ExportButtonHandoff,
  GraphQLFilter,
  SwitchTabViewData,
} from '../../@types';
import CustomViewContainer from '../CustomViewContainer';
import { DataViewContainer } from '../DataViewContainer';
import ExportDataButtonContainer from '../DataViewContainer/components/ExportDataButtonContainer';
import { FilterViewContainer } from '../FilterViewContainer';
import LayoutViewContainer from '../LayoutViewContainer';
import { TextViewContainer } from '../TextViewContainer';

import styles from './SwitchTabViewContainer.module.scss';

export interface Props extends React.HTMLAttributes<HTMLElement> {
  data: SwitchTabViewData;
  customizations?: Customizations;
  baseFilter?: GraphQLFilter;
  permissionFilter?: GraphQLFilter;
}

const mapComponent = (
  d: SwitchTabViewData['items'][0],
  customizations: Customizations | undefined,
  baseFilter: GraphQLFilter | undefined,
  permissionFilter: GraphQLFilter | undefined,
  onExportReady?: (handoff: ExportButtonHandoff | null) => void,
) => {
  // Page filters
  if (d.type === 'filter-view') {
    return (
      <FilterViewContainer
        key={d.id}
        data={d}
        baseFilter={baseFilter}
        permissionFilter={permissionFilter}
      />
    );
  }

  // Data views
  if (d.type === 'data-view') {
    return (
      <DataViewContainer
        key={d.id}
        data={d}
        customizations={customizations}
        baseFilter={baseFilter}
        permissionFilter={permissionFilter}
        onExportReady={onExportReady}
      />
    );
  }

  // Text views
  if (d.type === 'text-view') {
    return <TextViewContainer key={d.id} data={d} />;
  }

  // Layout views
  if (d.type === 'layout-view') {
    return (
      <LayoutViewContainer
        key={d.id}
        data={d}
        customizations={customizations}
        baseFilter={baseFilter}
        permissionFilter={permissionFilter}
      />
    );
  }

  // Custom views
  if (d.type === 'custom-view') {
    return (
      <CustomViewContainer
        key={d.id}
        data={d}
        customizations={customizations}
        baseFilter={baseFilter}
        permissionFilter={permissionFilter}
      />
    );
  }

  return null;
};

/**
 * <SwitchTabViewContainer> page
 */
export const SwitchTabViewContainer: React.FunctionComponent<Props> = ({
  className,
  style,
  data: {
    className: configClassName,
    title,
    tooltipContent,
    style: configStyle,
    options,
    initialTab,
    items,
  },
  baseFilter,
  permissionFilter,
  customizations,
  ...htmlElementProps
}) => {
  // Populated only when `options.exportInHeader` is set — the currently active
  // tab's own `DataViewContainer` reports its export config here instead of
  // rendering its own button inline.
  const [exportHandoff, setExportHandoff] =
    React.useState<ExportButtonHandoff | null>(null);

  return (
    <div className={className} style={style} {...htmlElementProps}>
      <Surface
        className={classNames(styles.surface, {
          'on-surface': options?.renderOnSurface === true,
        })}
        padding="sm"
      >
        <div className="d-flex flex-items-center flex-justify-between gap-2 mb-3">
          <div className="d-flex flex-items-center gap-2">
            {title && (
              <Heading as="h2" size="sm2" weight="bold">
                {title}
              </Heading>
            )}
            {tooltipContent && <CustomTooltipInfo info={tooltipContent} />}
          </div>

          {exportHandoff && (
            <ExportDataButtonContainer
              config={exportHandoff.config}
              filters={exportHandoff.filters}
              customQueryVariables={exportHandoff.customQueryVariables}
              data={exportHandoff.data}
              disabled={exportHandoff.disabled}
            />
          )}
        </div>

        <SwitchTab
          initialTab={initialTab}
          className={configClassName}
          style={configStyle}
          tabs={items.map((d) => ({
            id: d.id,
            label: d.label,
            component: mapComponent(
              d,
              customizations,
              baseFilter,
              permissionFilter,
              options?.exportInHeader ? setExportHandoff : undefined,
            ),
          }))}
        />
      </Surface>
    </div>
  );
};

export default SwitchTabViewContainer;
